import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Megaphone, Send, Trash2, Play, Pause, CalendarClock, Loader2, MessageSquareText, Users } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "./ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { personMatch } from "../lib/searchText";

export const AlertsPanel = () => {
  const { t, dir } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [expiry, setExpiry] = useState("");
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [bcTitle, setBcTitle] = useState("");
  const [bcBody, setBcBody] = useState("");
  const [bcSending, setBcSending] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [bcMode, setBcMode] = useState("all"); // all | branch | custom
  const [bcBranch, setBcBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [emps, setEmps] = useState([]);
  const [selEmps, setSelEmps] = useState([]);
  const [empSearch, setEmpSearch] = useState("");

  const load = useCallback(async () => {
    try { const d = await api.listAlerts(); setItems(d.items || []); }
    catch { toast.error(t("dash.alerts.loadFail")); }
    finally { setLoading(false); }
  }, [t]);

  const loadBroadcasts = useCallback(async () => {
    try { const d = await api.listBroadcasts(); setBroadcasts(d.items || []); } catch { /* ignore */ }
  }, []);

  const loadTargets = useCallback(async () => {
    try {
      const b = await api.branches();
      const raw = Array.isArray(b) ? b : (b?.branches || []);
      setBranches(raw.map((x) => (typeof x === "string" ? x : x.branch)).filter(Boolean));
    } catch { /* ignore */ }
    try {
      const e = await api.listEmployees();
      const list = Array.isArray(e) ? e : (e?.employees || e?.items || []);
      setEmps(list);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); loadBroadcasts(); loadTargets(); }, [load, loadBroadcasts, loadTargets]);

  const sendBroadcast = async () => {
    if (!bcTitle.trim()) { toast.error("اكتب نص الرسالة أولًا"); return; }
    if (bcMode === "branch" && !bcBranch) { toast.error("اختر الفرع"); return; }
    if (bcMode === "custom" && selEmps.length === 0) { toast.error("اختر مندوبًا واحدًا على الأقل"); return; }
    setBcSending(true);
    try {
      const branch = bcMode === "branch" ? bcBranch : "all";
      const employee_ids = bcMode === "custom" ? selEmps : null;
      const r = await api.sendBroadcast(bcTitle.trim(), bcBody.trim(), branch, employee_ids);
      setBcTitle(""); setBcBody(""); setSelEmps([]);
      toast.success(`تم إرسال الرسالة إلى ${r.sent} موظف`);
      loadBroadcasts();
    } catch { toast.error("تعذّر إرسال الرسالة"); }
    finally { setBcSending(false); }
  };

  const toggleEmp = (id) => setSelEmps((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const filteredEmps = emps.filter((e) => personMatch(e, empSearch));

  const send = async () => {
    if (!message.trim()) { toast.error(t("dash.alerts.emptyMsgErr")); return; }
    setSaving(true);
    try {
      await api.createAlert(message.trim(), expiry || null);
      setMessage(""); setExpiry("");
      toast.success(t("dash.alerts.added"));
      load();
    } catch { toast.error(t("dash.alerts.saveFail")); }
    finally { setSaving(false); }
  };

  const toggle = async (a) => {
    try { await api.updateAlert(a.id, { active: !a.active }); load(); }
    catch { toast.error(t("dash.alerts.saveFail")); }
  };

  const doDelete = async () => {
    if (!delTarget) return;
    try { await api.deleteAlert(delTarget.id); toast.success(t("dash.alerts.deleted")); }
    catch { toast.error(t("dash.alerts.saveFail")); }
    finally { setDelTarget(null); load(); }
  };

  const activeMsgs = items.filter((a) => a.active).map((a) => a.message);
  const previewUnit = [];
  let previewAcc = "";
  while (activeMsgs.length && previewAcc.length < 140) {
    activeMsgs.forEach((m) => { previewUnit.push(m); });
    previewAcc += activeMsgs.join("   ") + "   ";
  }
  const previewDur = Math.max(20, Math.min(80, Math.round((previewAcc.length || 1) * 0.35)));

  return (
    <div className="space-y-5" dir={dir}>
      <div className="flex items-center gap-2.5">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white shrink-0">
          <Megaphone className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-heading font-bold text-lg text-slate-900">{t("dash.alerts.title")}</h2>
          <p className="text-sm text-slate-500">{t("dash.alerts.subtitle")}</p>
        </div>
      </div>

      {/* Live preview */}
      {activeMsgs.length > 0 && (
        <div>
          <div className="text-xs text-slate-400 mb-1.5">{t("dash.alerts.previewTitle")} · {t("dash.alerts.previewNote")}</div>
          <div className="rounded-xl overflow-hidden shadow-sm">
            <div className="bg-gradient-to-r from-red-600 via-orange-500 to-red-600 text-white flex items-stretch">
              <div className="flex items-center px-3 bg-black/15 shrink-0"><Megaphone className="h-4 w-4" /></div>
              <div dir="ltr" className="wz-marquee-viewport relative flex-1 overflow-hidden py-2" style={{ "--wz-marquee-duration": `${previewDur}s` }}>
                <div className={`wz-marquee-track ${dir === "rtl" ? "wz-marquee-rtl" : ""}`} dir="ltr">
                  {[0, 1].map((k) => (
                    <span key={k} className="inline-flex items-center" aria-hidden={k === 1}>
                      {previewUnit.map((msg, i) => (
                        <span key={i} className="inline-flex items-center text-sm font-semibold px-4 whitespace-nowrap">
                          <span className="mx-2 h-1.5 w-1.5 rounded-full bg-white/70 shrink-0" />{msg}
                        </span>
                      ))}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Composer */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
        <div>
          <Label className="text-sm font-semibold text-slate-700">{t("dash.alerts.newLabel")}</Label>
          <Textarea data-testid="alert-message-input" value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder={t("dash.alerts.placeholder")} rows={2} className="mt-1.5 resize-none" />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <Label className="text-xs text-slate-500 flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" />{t("dash.alerts.expiryLabel")}</Label>
            <Input data-testid="alert-expiry-input" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="mt-1 h-10" />
          </div>
          <Button data-testid="alert-send-button" onClick={send} disabled={saving}
            className="bg-red-600 hover:bg-red-700 h-10 shrink-0">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mx-1.5" /> : <Send className="h-4 w-4 mx-1.5" />}
            {saving ? t("dash.alerts.sending") : t("dash.alerts.send")}
          </Button>
        </div>
      </div>

      {/* Broadcast direct message to employees */}
      <div className="rounded-2xl border border-blue-200 bg-blue-50/40 p-4 space-y-3" data-testid="broadcast-card">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shrink-0">
            <MessageSquareText className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-slate-900">رسالة جماعية للمناديب</h3>
            <p className="text-xs text-slate-500">تصل كإشعار داخل بوابة كل موظف + إشعار فوري على جواله (إن فعّله).</p>
          </div>
        </div>
        <Input data-testid="broadcast-title-input" value={bcTitle} onChange={(e) => setBcTitle(e.target.value)}
          placeholder="عنوان الرسالة (مثال: تنبيه هام من الإدارة)" className="bg-white" />
        <Textarea data-testid="broadcast-body-input" value={bcBody} onChange={(e) => setBcBody(e.target.value)}
          placeholder="نص الرسالة (اختياري)" rows={2} className="bg-white resize-none" />

        {/* Targeting */}
        <div className="space-y-2">
          <div className="inline-flex items-center gap-1 rounded-lg bg-white border border-slate-200 p-1 w-full sm:w-auto">
            {[["all", "الجميع"], ["branch", "فرع"], ["custom", "مناديب محددين"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`broadcast-mode-${v}`} onClick={() => setBcMode(v)}
                className={`flex-1 sm:flex-none px-3 py-1.5 text-xs rounded-md transition ${bcMode === v ? "bg-blue-600 text-white font-semibold" : "text-slate-500 hover:bg-slate-50"}`}>
                {l}
              </button>
            ))}
          </div>
          {bcMode === "branch" && (
            <Select value={bcBranch} onValueChange={setBcBranch}>
              <SelectTrigger data-testid="broadcast-branch-select" className="bg-white h-10"><SelectValue placeholder="اختر الفرع" /></SelectTrigger>
              <SelectContent position="item-aligned">
                {branches.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">لا توجد فروع</div>}
                {branches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {bcMode === "custom" && (
            <div className="rounded-xl border border-slate-200 bg-white p-2 space-y-2">
              <Input data-testid="broadcast-emp-search" value={empSearch} onChange={(e) => setEmpSearch(e.target.value)}
                placeholder="ابحث بالاسم أو الإقامة أو المعرف أو الجوال…" className="h-9" />
              <div className="text-[11px] text-slate-400">المحدّدون: {selEmps.length}</div>
              <div className="max-h-44 overflow-y-auto space-y-1">
                {filteredEmps.map((e) => {
                  const on = selEmps.includes(e.id);
                  return (
                    <button key={e.id} type="button" data-testid={`broadcast-emp-${e.id}`} onClick={() => toggleEmp(e.id)}
                      className={`w-full flex items-center justify-between rounded-lg px-3 py-1.5 text-sm transition ${on ? "bg-blue-50 border border-blue-200" : "hover:bg-slate-50 border border-transparent"}`}>
                      <span className="truncate">{e.name}{e.branch ? <span className="text-[10px] text-slate-400"> · {e.branch}</span> : null}</span>
                      <span className={`h-4 w-4 rounded grid place-items-center text-[10px] ${on ? "bg-blue-600 text-white" : "border border-slate-300"}`}>{on ? "✓" : ""}</span>
                    </button>
                  );
                })}
                {filteredEmps.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">لا نتائج</div>}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-slate-400 flex items-center gap-1"><Users className="h-3.5 w-3.5" />
            {bcMode === "all" ? "تُرسل لجميع الموظفين" : bcMode === "branch" ? "تُرسل لفرع محدد" : `تُرسل لـ ${selEmps.length} مندوب`}</span>
          <Button data-testid="broadcast-send-button" onClick={sendBroadcast} disabled={bcSending}
            className="bg-blue-600 hover:bg-blue-700 h-10 shrink-0 gap-1.5">
            {bcSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {bcSending ? "جارٍ الإرسال…" : "إرسال"}
          </Button>
        </div>
        {broadcasts.length > 0 && (
          <div className="pt-1 space-y-1.5" data-testid="broadcast-history">
            <div className="text-[11px] font-semibold text-slate-500">آخر الرسائل المرسلة</div>
            {broadcasts.slice(0, 5).map((b) => (
              <div key={b.id} data-testid={`broadcast-row-${b.id}`} className="rounded-lg bg-white border border-slate-100 px-3 py-2 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{b.title}</div>
                  {b.body && <div className="text-[11px] text-slate-400 truncate">{b.body}</div>}
                </div>
                <span className="text-[10px] text-slate-400 shrink-0 whitespace-nowrap">{b.count} موظف · {String(b.created_at).slice(0, 10)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* List */}
      <div>
        <div className="text-sm font-heading font-bold text-slate-700 mb-2">{t("dash.alerts.listTitle")}</div>
        {loading ? (
          <div className="py-10 text-center text-slate-400">{t("dash.alerts.loadFail")}</div>
        ) : items.length === 0 ? (
          <div data-testid="alerts-empty" className="py-10 text-center text-slate-400 border border-dashed border-slate-200 rounded-2xl">{t("dash.alerts.empty")}</div>
        ) : (
          <div className="space-y-2">
            {items.map((a) => (
              <motion.div key={a.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                data-testid={`alert-row-${a.id}`}
                className={`rounded-xl border p-3 flex items-start gap-3 ${a.active ? "border-red-100 bg-red-50/40" : "border-slate-200 bg-slate-50/60"}`}>
                <span className={`mt-0.5 shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full ${a.active ? "bg-red-100 text-red-700" : "bg-slate-200 text-slate-500"}`}>
                  {a.active ? t("dash.alerts.active") : t("dash.alerts.inactive")}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800 font-medium break-words">{a.message}</div>
                  <div className="text-[11px] text-slate-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span>{a.expires_at ? t("dash.alerts.expiresOn", { d: String(a.expires_at).slice(0, 10) }) : t("dash.alerts.noExpiry")}</span>
                    {a.created_by && <span>{t("dash.alerts.by", { n: a.created_by })}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button data-testid={`alert-toggle-${a.id}`} variant="outline" size="sm" onClick={() => toggle(a)} className="h-8">
                    {a.active ? <Pause className="h-3.5 w-3.5 mx-1" /> : <Play className="h-3.5 w-3.5 mx-1" />}
                    {a.active ? t("dash.alerts.deactivate") : t("dash.alerts.activate")}
                  </Button>
                  <Button data-testid={`alert-delete-${a.id}`} variant="ghost" size="sm" onClick={() => setDelTarget(a)}
                    className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50 px-2">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={Boolean(delTarget)} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent dir={dir} className="font-body">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">{t("dash.alerts.delTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("dash.alerts.delDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogAction data-testid="alert-confirm-delete" onClick={doDelete} className="bg-red-600 hover:bg-red-700">{t("dash.alerts.confirmDel")}</AlertDialogAction>
            <AlertDialogCancel>{t("dash.alerts.cancel")}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
